import React, { useMemo, useEffect, useState } from 'react';
import { DataManager } from '../../services/dataManager';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, ResponsiveContainer } from 'recharts';
import { getAssessmentLabel, getAssessmentIcon } from '../../constants/assessmentTypes';
import Button from '../common/Button';
import ManualQuestionnaireModal from '../counselor/ManualQuestionnaireModal';

const WheelOfLifeEvolution = ({ client, isCompact = false }) => {
    const [wheelData, setWheelData] = useState([]);
    const [isEditingLabels, setIsEditingLabels] = useState(false);
    const [customLabels, setCustomLabels] = useState(client.wheelLabels || {});
    const [editingBaseline, setEditingBaseline] = useState(null);
    const [showNewModal, setShowNewModal] = useState(false);

    // Standardized Capitalized Keys (aligned with DB and Client UI)
    const DEFAULT_AREAS = {
        Salute: 'Salute',
        Lavoro: 'Lavoro',
        Relazioni: 'Relazioni',
        Crescita: 'Crescita',
        Finanze: 'Finanze',
        Ambiente: 'Ambiente',
        Emozioni: 'Emozioni',
        Famiglia: 'Famiglia'
    };

    const loadBaselines = async () => {
        const baselines = await DataManager.getAllBaselines(client.id);

        // Filter and sort
        const wheelsWithDates = baselines
            .filter(b => b.wheelOfLife && Object.keys(b.wheelOfLife).length > 0)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
            .map((b, idx) => ({
                id: b.id,
                index: idx + 1,
                timestamp: b.timestamp,
                date: new Date(b.timestamp).toLocaleDateString('it-IT', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }),
                type: b.type || 'T0',
                baselineType: b.baselineType,
                wheelOfLife: b.wheelOfLife,
                anamnesis: b.anamnesis,
                qualitative: b.qualitative
            }));

        setWheelData(wheelsWithDates);
    };

    useEffect(() => {
        if (client.id) {
            loadBaselines();
        }
    }, [client.id, client.wheelLabels]);

    const handleSaveLabels = async () => {
        await DataManager.updateClientWheelLabels(client.id, customLabels);
        setIsEditingLabels(false);
        client.wheelLabels = customLabels;
    };

    const handleDeleteBaseline = async (id) => {
        if (confirm("Sei sicuro di voler eliminare questa compilazione? L'azione è irreversibile.")) {
            await DataManager.deleteBaseline(id);
            loadBaselines();
        }
    };

    // Prepare data for the radar chart
    const radarData = useMemo(() => {
        if (wheelData.length === 0) return [];

        const areaKeys = Object.keys(DEFAULT_AREAS);

        return areaKeys.map(key => {
            const label = customLabels[key] || customLabels[key.toLowerCase()] || DEFAULT_AREAS[key];

            const dataPoint = {
                area: label,
                fullMark: 10
            };

            wheelData.forEach((wheel) => {
                const dbKey = Object.keys(wheel.wheelOfLife).find(k => k.toLowerCase() === key.toLowerCase());

                let value = 0;
                if (dbKey) {
                    value = wheel.wheelOfLife[dbKey];
                } else if (key === 'Emozioni') {
                    const divKey = Object.keys(wheel.wheelOfLife).find(k => k.toLowerCase() === 'divertimento');
                    if (divKey) value = wheel.wheelOfLife[divKey];
                }

                dataPoint[`comp_${wheel.index}`] = value || 0;
            });

            return dataPoint;
        });
    }, [wheelData, customLabels]);

    const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#a78bfa', '#fb923c'];

    // Determine the next standard type (T0 if empty, T1 if 1, T2 if 2, else TX)
    const getNextType = () => {
        if (wheelData.length === 0) return 'T0';
        if (wheelData.length === 1) return 'T1';
        if (wheelData.length === 2) return 'T2';
        return `T${wheelData.length}`;
    };

    const averages = wheelData.map(wheel => {
        const values = Object.values(wheel.wheelOfLife);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        return { ...wheel, average: avg.toFixed(1) };
    });

    return (
        <div className="space-y-6">
            {!isCompact && (
                <div className="bg-white p-6 rounded-3xl border border-brand-100 flex justify-between items-center shadow-sm">
                    <div>
                        <h3 className="text-xl font-black text-brand-900 mb-1 flex items-center gap-2">
                            <span>🎡</span> Evoluzione Ruota della Vita
                        </h3>
                        <p className="text-xs text-brand-700 font-medium">Confronto grafico delle aree di sviluppo nel tempo</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowNewModal(true)}
                            className="px-4 py-2 bg-slate-800 text-white rounded-xl text-xs font-bold hover:bg-black transition flex items-center gap-2 shadow-lg"
                        >
                            <span>➕</span> Nuova Ruota
                        </button>
                        <button
                            onClick={() => setIsEditingLabels(!isEditingLabels)}
                            className="p-3 bg-brand-50 text-brand-600 rounded-2xl hover:shadow-md transition border border-brand-100"
                            title="Impostazioni Etichette"
                        >
                            {isEditingLabels ? '✕' : '⚙️'}
                        </button>
                    </div>
                </div>
            )}

            {isEditingLabels && (
                <div className="bg-white p-6 rounded-3xl shadow-xl border-2 border-brand-100 animate-slide-down">
                    <h4 className="font-black text-slate-800 text-sm mb-4 uppercase tracking-widest">Etichette Personalizzate</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        {Object.keys(DEFAULT_AREAS).map(key => (
                            <div key={key}>
                                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">{key}</label>
                                <input
                                    type="text"
                                    className="w-full p-2 text-sm bg-slate-50 border-2 border-transparent rounded-xl focus:bg-white focus:border-brand-200 outline-none transition"
                                    value={customLabels[key] || ''}
                                    placeholder={key}
                                    onChange={(e) => setCustomLabels({ ...customLabels, [key]: e.target.value })}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-end gap-3">
                        <button onClick={() => { setCustomLabels(client.wheelLabels || {}); setIsEditingLabels(false); }} className="px-4 py-2 text-xs font-bold text-slate-400">Annulla</button>
                        <Button onClick={handleSaveLabels} className="px-6 py-2 text-xs">Salva Modifiche</Button>
                    </div>
                </div>
            )}

            {wheelData.length === 0 ? (
                <div className={`${isCompact ? 'p-6' : 'p-12'} text-center bg-brand-50 rounded-[40px] border-2 border-dashed border-brand-200`}>
                    <div className={`${isCompact ? 'text-4xl mb-3' : 'text-7xl mb-6'}`}>🎡</div>
                    <h3 className={`${isCompact ? 'text-sm' : 'text-xl'} font-black text-brand-900 mb-2`}>Nessuna Ruota</h3>
                    {!isCompact && (
                        <>
                            <p className="text-brand-700 text-sm mb-8 max-w-xs mx-auto">Inizia registrando la prima valutazione per monitorare il benessere del cliente.</p>
                            <button
                                onClick={() => setShowNewModal(true)}
                                className="px-8 py-4 bg-brand-600 text-white rounded-[20px] font-black uppercase tracking-widest hover:bg-brand-700 shadow-2xl shadow-brand-200 transition transform active:scale-95"
                            >
                                Crea Prima Ruota
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <>
                    <div className={`${isCompact ? 'p-2' : 'bg-white p-8 rounded-[32px] shadow-sm border border-slate-100'}`}>
                        <ResponsiveContainer width="100%" height={isCompact ? 180 : 450}>
                            <RadarChart data={radarData}>
                                <PolarGrid stroke="#f1f5f9" />
                                <PolarAngleAxis
                                    dataKey="area"
                                    tick={{ fill: '#64748b', fontSize: 10, fontWeight: '900', letterSpacing: '0.05em' }}
                                />
                                <PolarRadiusAxis
                                    angle={90}
                                    domain={[0, 10]}
                                    tick={{ fill: '#cbd5e1', fontSize: 9 }}
                                />

                                {wheelData.map((wheel, idx) => (
                                    <Radar
                                        key={wheel.id}
                                        name={wheel.index.toString()}
                                        dataKey={`comp_${wheel.index}`}
                                        stroke={colors[idx % colors.length]}
                                        fill={colors[idx % colors.length]}
                                        fillOpacity={0.05}
                                        strokeWidth={3}
                                    />
                                ))}

                                <Legend
                                    wrapperStyle={{ paddingTop: isCompact ? '10px' : '30px' }}
                                    iconType="circle"
                                    formatter={(value) => <span className={`${isCompact ? 'text-[8px]' : 'text-xs'} font-black text-slate-600`}>{isCompact ? `C${value}` : `Compilazione ${value}`}</span>}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    </div>

                    {!isCompact && (
                        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
                            <h4 className="text-sm font-black text-slate-800 mb-6 uppercase tracking-widest flex items-center gap-2">
                                <span>📑</span> Logs Compilazioni
                            </h4>
                            <div className="space-y-3">
                                {averages.map((wheel, idx) => (
                                    <div key={wheel.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-transparent hover:border-brand-100 transition group">
                                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black italic shadow-lg" style={{ backgroundColor: colors[idx % colors.length] }}>
                                            {wheel.index}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-baseline gap-2">
                                                <span className="font-black text-slate-800 text-sm tracking-tight">{wheel.date}</span>
                                                <span className="text-[10px] font-black uppercase text-slate-400">
                                                    {getAssessmentIcon(wheel.type)} {getAssessmentLabel(wheel.type)}
                                                </span>
                                            </div>
                                            <div className="text-[11px] font-bold text-brand-600 mt-0.5">Media Punteggio: {wheel.average}/10</div>
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                                            <button onClick={() => setEditingBaseline(wheel)} className="px-4 py-2 bg-white text-slate-600 rounded-xl hover:shadow-md transition text-[10px] font-black uppercase tracking-wider">Modifica</button>
                                            <button onClick={() => handleDeleteBaseline(wheel.id)} className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition text-[10px] font-black uppercase tracking-wider">Elimina</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Modal for NEW compilation */}
            {showNewModal && (
                <ManualQuestionnaireModal
                    client={client}
                    assessmentType={getNextType()}
                    onClose={() => setShowNewModal(false)}
                    onSave={() => {
                        setShowNewModal(false);
                        loadBaselines();
                    }}
                />
            )}

            {/* Modal for EDITING existing baseline */}
            {editingBaseline && (
                <ManualQuestionnaireModal
                    client={client}
                    assessmentType={editingBaseline.baselineType}
                    initialData={editingBaseline}
                    onClose={() => setEditingBaseline(null)}
                    onSave={() => {
                        setEditingBaseline(null);
                        loadBaselines();
                    }}
                />
            )}
        </div>
    );
};

export default WheelOfLifeEvolution;
